import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  Platform,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { formatCurrency } from '../services/paymentGateway';
import { listTournaments as listTournamentsDb } from '../services/database';

const TournamentsScreen = ({ navigation }) => {
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clubTournaments, setClubTournaments] = useState([]);
  const [featuredTournaments, setFeaturedTournaments] = useState([]);

  useEffect(() => {
    if (appUser) {
      fetchTournaments();
    }
  }, [appUser]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      
      // Fetch all tournaments
      const allTournaments = await listTournamentsDb({ limit: 20 });
      
      // Convert snake_case to camelCase
      const formattedTournaments = allTournaments.map(t => ({
        id: t.id,
        name: t.name,
        description: t.description,
        status: t.status,
        type: t.type,
        totalRounds: t.total_rounds,
        currentRound: t.current_round,
        startTime: t.start_time,
        allowPhones: t.allow_phones,
        projectorCode: t.projector_code,
        isPaid: t.is_paid,
        entryFee: t.entry_fee,
        clubId: t.club_id,
        club: t.club ? {
          id: t.club.id,
          clubName: t.club.club_name,
        } : null,
      }));
      
      setFeaturedTournaments(formattedTournaments);
      
      // Filter for club tournaments if user is a club
      if (appUser?.accountType === 'CLUB') {
        const myTournaments = formattedTournaments.filter(t => t.clubId === appUser.id);
        setClubTournaments(myTournaments);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      showToast('Failed to load tournaments', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchTournaments();
  };

  // Dynamic styles
  const dynamicStyles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    headerSubtitle: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    section: {
      marginHorizontal: 16,
      marginBottom: 16,
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: isDarkMode ? 0.3 : (Platform.OS === 'ios' ? 0.08 : 0.2),
      shadowRadius: 12,
      elevation: 4,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    tournamentCard: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 14,
      padding: 16,
      marginBottom: 12,
    },
    tournamentTitle: {
      fontSize: 17,
      fontWeight: '600',
      color: colors.text,
    },
    tournamentDescription: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 10,
    },
    metaText: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    emptyTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textSecondary,
      marginTop: 12,
    },
    emptySubtitle: {
      fontSize: 13,
      color: colors.textLight,
      marginTop: 4,
      textAlign: 'center',
      paddingHorizontal: 20,
    },
    toolsCard: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 14,
      overflow: 'hidden',
    },
    toolRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderLight,
    },
    toolTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    toolSubtitle: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    centered: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
  };

  const renderTournamentCard = (item, isClubOwned = false) => (
    <TouchableOpacity
      key={item.id}
      style={dynamicStyles.tournamentCard}
      onPress={() => navigation.navigate('TournamentDetail', { tournamentId: item.id })}
    >
      <View style={styles.tournamentHeader}>
        <Text style={dynamicStyles.tournamentTitle}>{item.name}</Text>
        <View style={[styles.statusBadge, styles[`status${item.status}`]]}>
          <Text style={styles.statusText}>{item.status.replace('_', ' ')}</Text>
        </View>
      </View>
      <Text style={dynamicStyles.tournamentDescription} numberOfLines={2}>
        {item.description || 'No description provided'}
      </Text>
      <View style={styles.tournamentMeta}>
        <View style={styles.metaItem}>
          <Icon name="category" size={16} color={colors.textSecondary} />
          <Text style={dynamicStyles.metaText}>{item.type}</Text>
        </View>
        <View style={styles.metaItem}>
          <Icon name="repeat" size={16} color={colors.textSecondary} />
          <Text style={dynamicStyles.metaText}>
            {item.currentRound || 0}/{item.totalRounds || '?'} Rounds
          </Text>
        </View>
        <View style={styles.metaItem}>
          <Icon name="payments" size={16} color={colors.textSecondary} />
          <Text style={dynamicStyles.metaText}>
            {item.isPaid ? formatCurrency(item.entryFee || 0) : 'Free'}
          </Text>
        </View>
        {item.club && !isClubOwned && (
          <View style={styles.metaItem}>
            <Icon name="groups" size={16} color={colors.textSecondary} />
            <Text style={dynamicStyles.metaText}>{item.club.clubName || 'Club'}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  const renderClubSection = () => {
    const hasProTier = appUser?.tier === 'PRO';
    const isClaimed = !!appUser?.claimedBy;

    return (
      <View style={dynamicStyles.section}>
        <View style={styles.sectionHeader}>
          <Text style={dynamicStyles.sectionTitle}>Your Club Tournaments</Text>
          <TouchableOpacity
            style={[styles.actionButton, (!hasProTier || !isClaimed) && styles.actionButtonDisabled]}
            onPress={() => {
              if (!isClaimed) {
                showToast('Claim your club to run tournaments', 'error');
                navigation.navigate('UserDetail', { userId: appUser.id });
                return;
              }
              if (!hasProTier) {
                showToast('Tournament tools are Pro tier only', 'error');
                navigation.navigate('UpgradeClub', { clubId: appUser.id });
                return;
              }
              navigation.navigate('TournamentWizard', { clubId: appUser.id });
            }}
            disabled={!hasProTier || !isClaimed}
          >
            <Icon name="sports-esports" size={18} color="white" />
            <Text style={styles.actionButtonText}>Run Tournament</Text>
          </TouchableOpacity>
        </View>
        {clubTournaments.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="event-available" size={48} color={colors.textLight} />
            <Text style={dynamicStyles.emptyTitle}>No tournaments yet</Text>
            <Text style={dynamicStyles.emptySubtitle}>
              Create your first tournament to engage club members
            </Text>
          </View>
        ) : (
          clubTournaments.map((item) => renderTournamentCard(item, true))
        )}
      </View>
    );
  };

  const renderFeatured = () => (
    <View style={dynamicStyles.section}>
      <Text style={dynamicStyles.sectionTitle}>Explore Tournaments</Text>
      {featuredTournaments.length === 0 ? (
        <View style={styles.emptyState}>
          <Icon name="search" size={48} color={colors.textLight} />
          <Text style={dynamicStyles.emptyTitle}>No public tournaments yet</Text>
          <Text style={dynamicStyles.emptySubtitle}>
            Check back soon for featured events
          </Text>
        </View>
      ) : (
        featuredTournaments.map((item) => renderTournamentCard(item))
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={dynamicStyles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  const isClub = appUser?.accountType === 'CLUB';

  return (
    <ScrollView
      style={dynamicStyles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
    >
      <View style={styles.header}>
        <Text style={dynamicStyles.headerTitle}>Tournaments</Text>
        <Text style={dynamicStyles.headerSubtitle}>
          Manage Swiss & round-robin events, projector mode, player pairings, and more.
        </Text>
      </View>

      {isClub && renderClubSection()}

      <View style={dynamicStyles.section}>
        <Text style={dynamicStyles.sectionTitle}>Player Tools</Text>
        <View style={dynamicStyles.toolsCard}>
          <TouchableOpacity
            style={dynamicStyles.toolRow}
            onPress={() => showToast('Open the player link shared by the director', 'info')}
          >
            <View style={styles.toolInfo}>
              <Icon name="smartphone" size={24} color="#94EB98" />
              <View>
                <Text style={dynamicStyles.toolTitle}>My Pairings & Standings</Text>
                <Text style={dynamicStyles.toolSubtitle}>
                  View your board assignments, standings, and report results if enabled.
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textLight} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[dynamicStyles.toolRow, { borderBottomWidth: 0 }]}
            onPress={() =>
              showToast('Projector links are available inside each tournament', 'info')
            }
          >
            <View style={styles.toolInfo}>
              <Icon name="live-tv" size={24} color="#FF9800" />
              <View>
                <Text style={dynamicStyles.toolTitle}>Projector Mode</Text>
                <Text style={dynamicStyles.toolSubtitle}>
                  Share real-time standings and pairings on a big screen.
                </Text>
              </View>
            </View>
            <Icon name="chevron-right" size={24} color={colors.textLight} />
          </TouchableOpacity>
        </View>
      </View>

      {renderFeatured()}

      <View style={{ height: 100 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  header: {
    padding: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#94EB98',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
  },
  actionButtonDisabled: {
    backgroundColor: '#d0d0d0',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  tournamentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  tournamentMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusBadge: {
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: 'white',
    textTransform: 'capitalize',
  },
  statusDRAFT: {
    backgroundColor: '#9E9E9E',
  },
  status_UPCOMING: {
    backgroundColor: '#42A5F5',
  },
  status_IN_PROGRESS: {
    backgroundColor: '#94EB98',
  },
  status_COMPLETED: {
    backgroundColor: '#8D6E63',
  },
  status_CANCELLED: {
    backgroundColor: '#EF5350',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  toolInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
});

export default TournamentsScreen;
